import React from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';

/**
 * Global Error Boundary
 * Catches runtime errors (like Proxy Polyfill crashes on legacy browsers)
 * and displays a user-friendly fallback UI.
 */
class GlobalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("GlobalErrorBoundary caught an error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            const errorMessage = this.state.error?.message || "Unknown Runtime Error";
            const isSyntaxError = this.state.error instanceof SyntaxError
                || errorMessage.includes('Unexpected token');

            return (
                <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white p-4 text-center">
                    <FaExclamationTriangle className="text-6xl text-yellow-500 mb-6" />
                    <h1 className="text-2xl md:text-3xl font-bold mb-4">
                        {isSyntaxError ? 'Unsupported Browser Detected' : 'Something Went Wrong'}
                    </h1>
                    <p className="max-w-md text-gray-300 mb-4">
                        {isSyntaxError
                            ? 'It looks like your browser is too old to run our latest features.'
                            : 'An unexpected error occurred. Please try reloading the page.'}
                    </p>
                    <p className="max-w-md text-gray-500 text-sm mb-8 break-all">
                        {errorMessage}
                    </p>
                    <div className="flex gap-4">
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-3 bg-primary text-white font-bold rounded-full hover:bg-primary/80 transition-colors"
                        >
                            Reload Page
                        </button>
                        {isSyntaxError && (
                            <a
                                href="https://browsehappy.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-6 py-3 border border-primary text-primary font-bold rounded-full hover:bg-primary/10 transition-colors"
                            >
                                Update Browser
                            </a>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default GlobalErrorBoundary;
